import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../../../lib/supabaseAdmin';
import { Building2, Eye, EyeOff, Save, RefreshCw, AlertCircle, Check, X, CreditCard as Edit2, ExternalLink, ArrowUpDown, ChevronDown, ChevronUp, Link2 } from 'lucide-react';
import BankOfferEditor from '../../../components/BankOfferEditor';

interface BankOffer {
  id: string;
  bank_id: string;
  bank_name: string;
  logo_url: string;
  slogan: string;
  is_sponsored: boolean;
  offers: string[];
  button_text: string;
  color: string;
  mobile_url: string | null;
  redirect_url: string;
  display_order: number;
  page_orders: any[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Bank {
  id: string;
  name: string;
  logo: string;
  offers: string[];
}

const BankOffersManagement = () => {
  const [bankOffers, setBankOffers] = useState<BankOffer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [editingOrder, setEditingOrder] = useState<string | null>(null);
  const [tempOrderValue, setTempOrderValue] = useState<number>(0);
  const [editingUrl, setEditingUrl] = useState<string | null>(null);
  const [tempUrlValue, setTempUrlValue] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchBankOffers();
  }, []);

  const fetchBankOffers = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabaseAdmin
        .from('bank_offers')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching bank offers:', error);
        setErrorMessage('Banka teklifleri yüklenirken hata oluştu');
        return;
      }

      setBankOffers(data || []);
    } catch (error) {
      console.error('Error fetching bank offers:', error);
      setErrorMessage('Banka teklifleri yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleBankStatus = async (bankId: string, currentStatus: boolean) => {
    try {
      setIsSaving(bankId);
      const { error } = await supabaseAdmin
        .from('bank_offers')
        .update({ is_active: !currentStatus, updated_at: new Date().toISOString() })
        .eq('bank_id', bankId);

      if (error) {
        console.error('Error updating bank status:', error);
        setErrorMessage('Banka durumu güncellenirken hata oluştu');
        return;
      }

      setBankOffers(prev =>
        prev.map(offer =>
          offer.bank_id === bankId
            ? { ...offer, is_active: !currentStatus }
            : offer
        )
      );

      setSuccessMessage(`${bankId} ${!currentStatus ? 'aktif' : 'pasif'} yapıldı`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error updating bank status:', error);
      setErrorMessage('Banka durumu güncellenirken hata oluştu');
    } finally {
      setIsSaving(null);
    }
  };

  const handleEditClick = (offer: BankOffer) => {
    setSelectedBank({
      id: offer.bank_id,
      name: offer.bank_name,
      logo: offer.logo_url,
      offers: offer.offers
    });
    setIsEditorOpen(true);
  };

  const handleSaveOffers = async (bankId: string, offers: string[]) => {
    try {
      setIsSaving(bankId);
      const { error } = await supabaseAdmin
        .from('bank_offers')
        .update({ offers, updated_at: new Date().toISOString() })
        .eq('bank_id', bankId);

      if (error) {
        console.error('Error updating bank offers:', error);
        setErrorMessage('Banka teklifleri güncellenirken hata oluştu');
        return;
      }

      setBankOffers(prev =>
        prev.map(offer =>
          offer.bank_id === bankId
            ? { ...offer, offers }
            : offer
        )
      );

      setSuccessMessage('Banka teklifleri başarıyla güncellendi');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error updating bank offers:', error);
      setErrorMessage('Banka teklifleri güncellenirken hata oluştu');
    } finally {
      setIsSaving(null);
    }
  };

  const handleOrderEdit = (bankId: string, currentOrder: number) => {
    setEditingOrder(bankId);
    setTempOrderValue(currentOrder);
  };

  const handleOrderSave = async (bankId: string) => {
    try {
      setIsSaving(bankId);

      // Get current bank offer to access page_orders
      const currentOffer = bankOffers.find(o => o.bank_id === bankId);
      if (!currentOffer) {
        setErrorMessage('Banka bulunamadı');
        return;
      }

      // Update page_orders to match the new display_order for uzaktan-musteri-edinimi page
      const updatedPageOrders = currentOffer.page_orders.map((po: any) => {
        if (po.page === 'uzaktan-musteri-edinimi') {
          return { ...po, displayOrder: tempOrderValue };
        }
        return po;
      });

      const { error } = await supabaseAdmin
        .from('bank_offers')
        .update({
          display_order: tempOrderValue,
          page_orders: updatedPageOrders,
          updated_at: new Date().toISOString()
        })
        .eq('bank_id', bankId);

      if (error) {
        console.error('Error updating display order:', error);
        setErrorMessage('Sıralama güncellenirken hata oluştu');
        return;
      }

      setBankOffers(prev =>
        prev.map(offer =>
          offer.bank_id === bankId
            ? { ...offer, display_order: tempOrderValue, page_orders: updatedPageOrders }
            : offer
        ).sort((a, b) => a.display_order - b.display_order)
      );

      setEditingOrder(null);
      setSuccessMessage('Sıralama başarıyla güncellendi');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error updating display order:', error);
      setErrorMessage('Sıralama güncellenirken hata oluştu');
    } finally {
      setIsSaving(null);
    }
  };

  const handleOrderCancel = () => {
    setEditingOrder(null);
    setTempOrderValue(0);
  };

  const handleUrlEdit = (bankId: string, currentUrl: string) => {
    setEditingUrl(bankId);
    setTempUrlValue(currentUrl);
  };

  const handleUrlSave = async (bankId: string) => {
    try {
      setIsSaving(bankId);
      const { error } = await supabaseAdmin
        .from('bank_offers')
        .update({
          redirect_url: tempUrlValue,
          updated_at: new Date().toISOString()
        })
        .eq('bank_id', bankId);

      if (error) {
        console.error('Error updating redirect URL:', error);
        setErrorMessage('Yönlendirme URL güncellenirken hata oluştu');
        return;
      }

      setBankOffers(prev =>
        prev.map(offer =>
          offer.bank_id === bankId
            ? { ...offer, redirect_url: tempUrlValue }
            : offer
        )
      );

      setEditingUrl(null);
      setSuccessMessage('Yönlendirme URL başarıyla güncellendi');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error updating redirect URL:', error);
      setErrorMessage('Yönlendirme URL güncellenirken hata oluştu');
    } finally {
      setIsSaving(null);
    }
  };

  const handleUrlCancel = () => {
    setEditingUrl(null);
    setTempUrlValue('');
  };

  const toggleRowExpansion = (bankId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(bankId)) {
        newSet.delete(bankId);
      } else {
        newSet.add(bankId);
      }
      return newSet;
    });
  };

  const toggleSponsoredStatus = async (bankId: string, currentStatus: boolean) => {
    try {
      setIsSaving(bankId);
      const { error } = await supabaseAdmin
        .from('bank_offers')
        .update({ is_sponsored: !currentStatus, updated_at: new Date().toISOString() })
        .eq('bank_id', bankId);

      if (error) {
        console.error('Error updating sponsored status:', error);
        setErrorMessage('Sponsorlu durumu güncellenirken hata oluştu');
        return;
      }

      setBankOffers(prev =>
        prev.map(offer =>
          offer.bank_id === bankId
            ? { ...offer, is_sponsored: !currentStatus }
            : offer
        )
      );

      setSuccessMessage(`${bankId} ${!currentStatus ? 'sponsorlu' : 'sponsorsuz'} yapıldı`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error('Error updating sponsored status:', error);
      setErrorMessage('Sponsorlu durumu güncellenirken hata oluştu');
    } finally {
      setIsSaving(null);
    }
  };

  const getBankColorClass = (color: string) => {
    switch (color) {
      case 'red': return 'bg-red-100 text-red-800 border-red-200';
      case 'green': return 'bg-green-100 text-green-800 border-green-200';
      case 'blue': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'purple': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Banka Teklif Yönetimi</h1>
          <p className="text-slate-600 mt-1">
            Banka tekliflerini aktif veya pasif yapabilirsiniz
          </p>
        </div>
        <button
          onClick={fetchBankOffers}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          Yenile
        </button>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check size={18} />
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle size={18} />
          {errorMessage}
          <button
            onClick={() => setErrorMessage(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F7BA45] mx-auto mb-4"></div>
            <p className="text-slate-600">Yükleniyor...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Banka
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Sıralama
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Sponsorlu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Fırsat Sayısı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {bankOffers.map((offer) => (
                  <React.Fragment key={offer.id}>
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleRowExpansion(offer.bank_id)}
                            className="p-1 hover:bg-slate-200 rounded transition-colors"
                            title="Detayları göster/gizle"
                          >
                            {expandedRows.has(offer.bank_id) ? (
                              <ChevronUp size={18} className="text-slate-600" />
                            ) : (
                              <ChevronDown size={18} className="text-slate-600" />
                            )}
                          </button>
                          <img
                            src={offer.logo_url}
                            alt={offer.bank_name}
                            className="h-8 w-auto object-contain"
                          />
                          <div>
                            <div className="font-medium text-slate-900">{offer.bank_name}</div>
                            <div className={`text-xs px-2 py-0.5 rounded-full border inline-block mt-1 ${getBankColorClass(offer.color)}`}>
                              {offer.color}
                            </div>
                          </div>
                        </div>
                      </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingOrder === offer.bank_id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            value={tempOrderValue}
                            onChange={(e) => setTempOrderValue(parseInt(e.target.value) || 0)}
                            className="w-20 px-2 py-1 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7BA45]"
                            min="0"
                          />
                          <button
                            onClick={() => handleOrderSave(offer.bank_id)}
                            disabled={isSaving === offer.bank_id}
                            className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                            title="Kaydet"
                          >
                            <Check size={18} />
                          </button>
                          <button
                            onClick={handleOrderCancel}
                            className="p-1 text-red-600 hover:text-red-700"
                            title="İptal"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="text-sm text-slate-900 font-medium">
                            {offer.display_order}
                          </div>
                          <button
                            onClick={() => handleOrderEdit(offer.bank_id, offer.display_order)}
                            className="p-1 text-slate-400 hover:text-slate-600"
                            title="Sıralamayı düzenle"
                          >
                            <ArrowUpDown size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleSponsoredStatus(offer.bank_id, offer.is_sponsored)}
                        disabled={isSaving === offer.bank_id}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all disabled:opacity-50 ${
                          offer.is_sponsored
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border border-yellow-200'
                            : 'bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-200'
                        }`}
                      >
                        {offer.is_sponsored ? (
                          <>
                            <Check size={14} />
                            Sponsorlu
                          </>
                        ) : (
                          <>
                            <X size={14} />
                            Sponsorsuz
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        {offer.offers.length} fırsat
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {offer.is_active ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          <Eye size={14} />
                          Aktif
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                          <EyeOff size={14} />
                          Pasif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditClick(offer)}
                          disabled={isSaving === offer.bank_id}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Edit2 size={16} />
                          Düzenle
                        </button>
                        <button
                          onClick={() => toggleBankStatus(offer.bank_id, offer.is_active)}
                          disabled={isSaving === offer.bank_id}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                            offer.is_active
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                        >
                          {isSaving === offer.bank_id ? (
                            <>
                              <RefreshCw size={16} className="animate-spin" />
                              Kaydediliyor...
                            </>
                          ) : offer.is_active ? (
                            <>
                              <EyeOff size={16} />
                              Pasif Yap
                            </>
                          ) : (
                            <>
                              <Eye size={16} />
                              Aktif Yap
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                    </tr>
                    {expandedRows.has(offer.bank_id) && (
                      <tr className="bg-slate-50">
                        <td colSpan={6} className="px-6 py-4">
                          <div className="space-y-4">
                            <div className="border-t border-slate-200 pt-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Link2 size={18} className="text-slate-600" />
                                  <h3 className="font-medium text-slate-900">Yönlendirme URL</h3>
                                </div>
                                {editingUrl !== offer.bank_id && (
                                  <button
                                    onClick={() => handleUrlEdit(offer.bank_id, offer.redirect_url)}
                                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                                  >
                                    <Edit2 size={14} />
                                    Düzenle
                                  </button>
                                )}
                              </div>

                              {editingUrl === offer.bank_id ? (
                                <div className="space-y-2">
                                  <textarea
                                    value={tempUrlValue}
                                    onChange={(e) => setTempUrlValue(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7BA45] font-mono text-sm"
                                    rows={3}
                                    placeholder="https://..."
                                  />
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleUrlSave(offer.bank_id)}
                                      disabled={isSaving === offer.bank_id}
                                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                      <Check size={16} />
                                      Kaydet
                                    </button>
                                    <button
                                      onClick={handleUrlCancel}
                                      className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors flex items-center gap-2"
                                    >
                                      <X size={16} />
                                      İptal
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-white rounded-lg p-3 border border-slate-200">
                                  <div className="flex items-start gap-2">
                                    <code className="flex-1 text-sm text-slate-700 break-all">
                                      {offer.redirect_url}
                                    </code>
                                    <a
                                      href={offer.redirect_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-700 flex-shrink-0"
                                      title="Yeni sekmede aç"
                                    >
                                      <ExternalLink size={16} />
                                    </a>
                                  </div>
                                </div>
                              )}
                            </div>

                            {offer.mobile_url && (
                              <div className="border-t border-slate-200 pt-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <Link2 size={18} className="text-slate-600" />
                                  <h3 className="font-medium text-slate-900">Mobil URL</h3>
                                </div>
                                <div className="bg-white rounded-lg p-3 border border-slate-200">
                                  <div className="flex items-start gap-2">
                                    <code className="flex-1 text-sm text-slate-700 break-all">
                                      {offer.mobile_url}
                                    </code>
                                    <a
                                      href={offer.mobile_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-700 flex-shrink-0"
                                      title="Yeni sekmede aç"
                                    >
                                      <ExternalLink size={16} />
                                    </a>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div className="border-t border-slate-200 pt-4">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-medium text-slate-900">Fırsatlar</h3>
                              </div>
                              <div className="space-y-2">
                                {offer.offers.map((offerText, idx) => (
                                  <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200 text-sm text-slate-700">
                                    {offerText}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {bankOffers.length === 0 && (
            <div className="text-center py-12">
              <Building2 size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">Henüz banka teklifi bulunmuyor</p>
            </div>
          )}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Bilgilendirme</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Pasif yapılan bankalar müşteri edinim sayfasında görünmez</li>
              <li>Aktif yapılan bankalar hemen görünür hale gelir</li>
              <li>Değişiklikler anında etkili olur</li>
            </ul>
          </div>
        </div>
      </div>

      <BankOfferEditor
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        bank={selectedBank}
        onSave={handleSaveOffers}
      />
    </div>
  );
};

export default BankOffersManagement;
